import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import GalleryLightbox from "../components/GalleryLightbox";
import CtaBanner from "../components/CtaBanner";
import { SERVICES_LIST, COMPANY_INFO } from "../data/siteData";

export default function ServiceDetailPage({ defaultSlug }) {
  const params = useParams();
  const slug = defaultSlug || params.slug || "tilbygning";

  const [pageData, setPageData] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);

  const fallbackService = SERVICES_LIST.find((s) => s.slug === slug) || SERVICES_LIST[0];

  useEffect(() => {
    setLoading(true);

    // Fetch CMS page content for this page_slug
    fetch(`https://api.dev.auxo.dk/public/v2/content/pages?page_slug=${slug}`)
      .then((res) => res.json())
      .then((data) => {
        if (data && data.items && data.items.length > 0) {
          const cmsItem = data.items[0];
          setPageData(cmsItem);
          document.title = cmsItem.meta_title || `${cmsItem.title} - Kærby Tømrer- & Snedkerforretning ApS`;

          const metaDesc = document.querySelector('meta[name="description"]');
          if (metaDesc && cmsItem.meta_description) {
            metaDesc.setAttribute("content", cmsItem.meta_description);
          }
        } else {
          document.title = `${fallbackService.title} - Kærby Tømrer- & Snedkerforretning ApS`;
        }
      })
      .catch(() => {
        document.title = `${fallbackService.title} - Kærby Tømrer- & Snedkerforretning ApS`;
      })
      .finally(() => setLoading(false));

    // Fetch associated CMS gallery by gallery_slug or category
    fetch(`https://api.dev.auxo.dk/public/v2/content/galleries`)
      .then((res) => res.json())
      .then((data) => {
        if (data && data.items) {
          const matched = data.items.find(
            (g) => g.gallery_slug === slug || g.gallery_slug?.includes(slug)
          );
          if (matched && matched.images) {
            setGalleryImages(matched.images);
          }
        }
      })
      .catch(() => {});
  }, [slug]);

  const displayTitle = pageData?.title || fallbackService.title;
  const displayImage = pageData?.featured_image?.[0] || fallbackService.image;

  return (
    <div className="bg-white min-h-screen space-y-0 text-stone-900">
      
      {/* Page Header / Breadcrumb Hero */}
      <section className="relative bg-zinc-900 text-white pt-20 sm:pt-24 pb-16 sm:pb-20 overflow-hidden border-b border-zinc-800 -mt-14 sm:-mt-16 z-0">
        <div className="absolute inset-0 z-0 opacity-25">
          <img
            src={displayImage}
            alt={displayTitle}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-900 via-zinc-900/90 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
          <nav className="flex items-center gap-2 text-xs sm:text-sm text-stone-400">
            <Link to="/" className="hover:text-[#ea580c] transition-colors">Forside</Link>
            <i className="fa-solid fa-chevron-right text-[10px]" aria-hidden="true" />
            <Link to="/ydelser" className="hover:text-[#ea580c] transition-colors">Ydelser</Link>
            <i className="fa-solid fa-chevron-right text-[10px]" aria-hidden="true" />
            <span className="text-[#ea580c] font-semibold">{displayTitle}</span>
          </nav>

          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white max-w-3xl uppercase">
            {displayTitle}
          </h1>
          <p className="text-stone-300 text-base sm:text-lg max-w-2xl">
            Professionel løsning udført af Kærby Tømrer- & Snedkerforretning ApS i Randers, Hadsund og Mariager.
          </p>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-16 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* Left Content Area */}
            <div className="lg:col-span-8 space-y-10">
              
              {/* Rich Text Body */}
              <div className="prose rich-content max-w-none text-stone-800">
                {pageData?.content ? (
                  <div dangerouslySetInnerHTML={{ __html: pageData.content }} />
                ) : (
                  <>
                    <h2>Professionel løsning til dit byggeprojekt</h2>
                    <p>{fallbackService.shortDesc}</p>
                    <p>
                      Hos <strong>Kærby Tømrer- & Snedkerforretning ApS</strong> har vi mange års erfaring med udførsel af håndværk i højeste faglige kvalitet. Vi rådgiver om materialevalg, konstruktioner og myndighedskrav, så du opnår den bedste og mest langtidsholdbare løsning til din bolig.
                    </p>
                    <h3>Hvorfor vælge os til opgaven?</h3>
                    <ul>
                      <li>Grundig faglig rådgivning fra start til slut</li>
                      <li>Kvalitetsmaterialer der holder i det danske klima</li>
                      <li>Aftaler, tidsplaner og tilbud overholdes altid</li>
                      <li>Grundig oprydning og pæn finish på hele byggepladsen</li>
                    </ul>
                    <p>
                      Vi udfører opgaver for private, erhverv og boligforeninger i Randers, Hadsund, Mariager, Gjerlev og omegn. Ring gerne til mester for en uforpligtende snak om dine muligheder.
                    </p>
                  </>
                )}
              </div>

            </div>

            {/* Right Sidebar */}
            <div className="lg:col-span-4 space-y-8">
              
              {/* Direct Call Box */}
              <div className="bg-zinc-900 text-white p-6 rounded-2xl border border-zinc-800 space-y-4 shadow-xl">
                <span className="text-[#ea580c] font-bold text-xs uppercase tracking-wider block">
                  Brug for rådgivning?
                </span>
                <h3 className="text-xl font-bold uppercase">Har du spørgsmål til {displayTitle.toLowerCase()}?</h3>
                <p className="text-stone-300 text-sm leading-relaxed">
                  Ring direkte til mester for en uforpligtende snak om dit projekt og tidsramme.
                </p>
                <a
                  href={`tel:${COMPANY_INFO.phoneRaw}`}
                  className="w-full flex items-center justify-center gap-3 bg-[#ea580c] hover:bg-[#c2410c] text-white font-bold uppercase tracking-wider py-3.5 px-4 rounded-md transition-colors whitespace-nowrap shadow-md"
                >
                  <i className="fa-solid fa-phone" aria-hidden="true" />
                  <span className="whitespace-nowrap">Ring 26 19 20 88</span>
                </a>
              </div>

            </div>

          </div>
        </div>
      </section>

      <CtaBanner />

    </div>
  );
}
